import { useCallback } from 'react'
import { IRowNode } from 'ag-grid-enterprise'
import { EnhancedStatementRow, FinancialRowType, checkRowHasDataForColumns } from '../utils/dataEnhancement'
import { StatementColumn } from '../types'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { FiscalYear } from '../../../types/fiscalYear/FiscalYear'

export interface UseRowFilteringReturn {
  isExternalFilterPresent: () => boolean
  doesExternalFilterPass: (node: IRowNode<EnhancedStatementRow>) => boolean
}

const isCurrentOrFutureMonths = (columns: StatementColumn[]) => {
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1 // getMonth() is 0-indexed

  return columns.some(col => {
    if (col.month === undefined || col.year === undefined) return false
    return col.year > currentYear || (col.year === currentYear && col.month >= currentMonth)
  })
}

const getPreviousYearGroupIds = (periodGroups: (PeriodGroup | FiscalYear)[]) => {
  const today = new Date()

  const currentIndex = periodGroups.findIndex(pg => {
    const start = new Date(pg.startDate)
    const end = pg.endDate ? new Date(pg.endDate) : new Date()
    return start <= today && end >= today
  })

  if (currentIndex < 0) return []

  const ids: number[] = []
  if (currentIndex > 0) {
    ids.push(periodGroups[currentIndex - 1].id)
  }
  ids.push(periodGroups[currentIndex].id)

  return ids
}

const hasDataInGroups = (row: EnhancedStatementRow, groupIds: number[]) => {
  for (const id of groupIds) {
    if (row.actuals?.groups?.[id]) return true
  }
  return false
}

export const useRowFiltering = (
  displayEmptyRows: boolean,
  columns: StatementColumn[],
  periodGroups?: (PeriodGroup | FiscalYear)[]
): UseRowFilteringReturn => {
  const isExternalFilterPresent = useCallback(() => !displayEmptyRows, [displayEmptyRows])

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<EnhancedStatementRow>) => {
      if (!node.data) return false

      const checkRowData = (row?: EnhancedStatementRow) => {
        if (!row) return false

        const hasColumnData = checkRowHasDataForColumns(row, columns)

        if (isCurrentOrFutureMonths(columns)) {
          const prevYearGroupIds = periodGroups ? getPreviousYearGroupIds(periodGroups) : []
          const hasPrevYearData = hasDataInGroups(row, prevYearGroupIds)

          return hasColumnData || hasPrevYearData
        }

        return hasColumnData
      }

      if (node.data.rowType === FinancialRowType.ACCOUNT) {
        if ((node.allChildrenCount ?? 0) > 1) return false
        return checkRowData(node.data)
      }

      if (
        node.data.rowType === FinancialRowType.DIMENSION_GROUP ||
        node.data.rowType === FinancialRowType.AMENDMENT_MENU ||
        node.data.rowType === FinancialRowType.REALLOCATION_MENU ||
        node.data.rowType === FinancialRowType.COUNTER_AMENDMENT_MENU ||
        node.data.rowType === FinancialRowType.INVESTMENT_MENU
      ) {
        return checkRowData(node?.parent?.data)
      }

      if (node.data.rowType === FinancialRowType.DIMENSION) {
        return checkRowData(node.data)
      }

      return checkRowData(node.data)
    },
    [periodGroups, columns]
  )

  return {
    isExternalFilterPresent,
    doesExternalFilterPass
  }
}
